import { useIntl } from "react-intl"
import { useRef, useState } from "react"
import { Combobox, CustomTable, CustomTableRef, EInputTypes, formatDate, getDate, GetItemsProps, InputDate, InputTextCustom } from "medere-ui-framework"
import { Column } from "primereact/column"
import { BudgetDTO, defaultBudgetDTO } from "../../dto/budget/BudgetDTO"
import './Budget.scss'
import { EBudgetGridOptions, EBudgetGridOptionsValues } from "../../enums/EBudgetGridOptions"
import { cancelBudget, getAllBudgets, getBudgetComplete, getBudgetStates } from "../../servicesHooks/BillHospitalization/useBillHospitalizationService"
import { ComboDataDTO, defaultAllComboDataDTO } from "../../dto/ComboDataDTO"

import { parseApiError } from "../../services/apiError"
import useNotify from "../../hooks/useNotify"
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog"
import React from "react"
import { useEntityService } from "../../servicesHooks/Entity/useEntityService"

/** "Descargar PDF" oculto por ahora a pedido de Tomás (2026-10-13); el modal y la descarga siguen funcionando. */
const SHOW_PDF_ACTION = false

const CANCELED_STATE = 5
const PREINVOICED_STATE = 6

/** Prefacturada o cancelada: ya no se edita, no genera órdenes ni se cancela. */
const isClosed = (budget: BudgetDTO) => budget.budgetstate === PREINVOICED_STATE || budget.budgetstate === CANCELED_STATE

interface HistoricBillHospitalizationViewProps {
    selectedAction: number;
    setSelectedAction: (action: number) => void;
    setSelectedBudget: (selectedBudget: BudgetDTO) => void;
    setOpenPrintBudgetModal: (value: boolean) => void;
}

export default function HistoricBillHospitalizationView({ selectedAction, setSelectedAction, setSelectedBudget, setOpenPrintBudgetModal }: HistoricBillHospitalizationViewProps) {

    const { getHealthEntityComboDataDTO } = useEntityService();
    const intl = useIntl();
    const queryRef = useRef<CustomTableRef>(null);
    const [filterAddressee, setFilterAddressee] = useState<string>("");
    const [filterHealthEntity, setFilterHealthEntity] = useState<ComboDataDTO>(defaultAllComboDataDTO);
    const [filterBudgetId, setFilterBudgetId] = useState<number | null>(null);
    const [filterDate, setFilterDate] = useState<Date>();
    const [filterDetail, setFilterDetail] = useState<string>("");
    const [filterState, setFilterState] = useState<ComboDataDTO>(defaultAllComboDataDTO);
    const notify = useNotify();

    /** Cancelar libera los ítems de la facturación para otra: se confirma antes. */
    function confirmCancel(budget: BudgetDTO) {
        confirmDialog({
            header: '¿Cancelar la facturación?',
            message: `Se cancela la facturación N.º ${budget.budget} de ${budget.addressee} y sus ítems quedan libres para otra facturación.`,
            acceptLabel: 'Cancelar facturación',
            acceptClassName: 'buttonMain',
            rejectLabel: 'Volver',
            rejectClassName: 'buttonMain2 p-button-outlined',
            className: 'nv-scope nv-confirm',
            accept: () => cancel(budget),
        });
    }

    function cancel(budget: BudgetDTO) {
        setSelectedAction(EBudgetGridOptionsValues[EBudgetGridOptions.CANCEL]);
        cancelBudget(budget.budget ?? -1)
            .then(() => {
                notify.success('Facturación cancelada');
                queryRef.current?.refreshTable?.();
            })
            .catch((error: unknown) => {
                const apiError = parseApiError(error);
                const isBusinessError = apiError.status >= 400 && apiError.status < 500;
                notify.error(isBusinessError ? apiError.message : 'No se pudo cancelar la facturación. Intentá de nuevo.');
            });
    }

    return (
        <React.Fragment>
            <ConfirmDialog />
            <CustomTable idTable="tablebillhospitalization" getItems={getAllBudgets}
                clearFiltersClassName="buttonControl"
                params={{
                    filterAddressee,
                    filterHealthEntity: filterHealthEntity.id,
                    filterBudgetId,
                    filterDate,
                    filterDetail,
                    filterState: filterState.id,
                    filterHospitalization: true,
                }}
                minItemWidth
                className='historicBudgetTable crudTable editable-cells-table'
                onClearFilters={() => {
                    setFilterAddressee("");
                    setFilterHealthEntity(defaultAllComboDataDTO);
                    setFilterBudgetId(null);
                    setFilterDate(undefined);
                    setFilterDetail("");
                    setFilterState(defaultAllComboDataDTO);
                }}
                rowButtonClassName=""
                buttonControllerLeft
                buttonController
                dropdownExtraButtons={[
                    {
                        text: intl.formatMessage({ id: 'Preview' }),
                        icon: 'pi pi-eye',
                        onClick: (selectedBudget: BudgetDTO) => {
                            getBudgetComplete(selectedBudget.budget ?? -1).then((res: BudgetDTO) => {
                                setSelectedBudget(res);
                                setSelectedAction(EBudgetGridOptionsValues[EBudgetGridOptions.PREVIEW]);
                            });
                        }
                    },
                    {
                        text: intl.formatMessage({ id: 'Edit' }),
                        icon: 'pi pi-pencil',
                        showWhen: (selectedBudget: BudgetDTO) => !isClosed(selectedBudget),
                        onClick: (selectedBudget: BudgetDTO) => {
                            getBudgetComplete(selectedBudget.budget ?? -1).then((res: BudgetDTO) => {
                                setSelectedBudget(res);
                                setSelectedAction(EBudgetGridOptionsValues[EBudgetGridOptions.EDIT]);
                            });
                        }
                    },
                    {
                        text: "Generar orden",
                        icon: 'pi pi-dollar',
                        showWhen: (selectedBudget: BudgetDTO) => !isClosed(selectedBudget),
                        onClick: (selectedBudget: BudgetDTO) => {
                            getBudgetComplete(selectedBudget.budget ?? -1).then((res: BudgetDTO) => {
                                setSelectedBudget(res);
                                setSelectedAction(EBudgetGridOptionsValues[EBudgetGridOptions.PREINVOICE]);
                            });
                        }
                    },
                    {
                        text: "Ver órdenes",
                        icon: 'pi pi-list',
                        showWhen: (selectedBudget: BudgetDTO) => selectedBudget.budgetstate === PREINVOICED_STATE,
                        onClick: (selectedBudget: BudgetDTO) => {
                            setSelectedBudget(selectedBudget);
                            setSelectedAction(EBudgetGridOptionsValues[EBudgetGridOptions.ORDERS]);
                        }
                    },
                    {
                        text: "Descargar PDF",
                        icon: 'pi pi-download',
                        showWhen: () => SHOW_PDF_ACTION,
                        onClick: (selectedBudget: BudgetDTO) => {
                            setSelectedBudget(selectedBudget);
                            setOpenPrintBudgetModal(true);
                        }
                    },
                    {
                        text: intl.formatMessage({ id: 'Cancel' }),
                        icon: 'pi pi-times',
                        showWhen: (selectedBudget: BudgetDTO) => !isClosed(selectedBudget) && (selectedAction != EBudgetGridOptionsValues[EBudgetGridOptions.CREATE] && selectedAction != EBudgetGridOptionsValues[EBudgetGridOptions.EDIT] && selectedAction != EBudgetGridOptionsValues[EBudgetGridOptions.DUPLICATE]),
                        onClick: (selectedBudget: BudgetDTO) => confirmCancel(selectedBudget)
                    }
                ]} ref={queryRef}>
                <Column field="addresseedesc" header={intl.formatMessage({ id: 'Addressee' })} className="columnMedium"
                    body={(bud: BudgetDTO) => <div className='text-left'>{bud.addressee}</div>}
                    filter
                    filterElement={
                        <InputTextCustom placeholder={intl.formatMessage({ id: "Addressee" })}
                            value={filterAddressee}
                            inputType={EInputTypes.TEXT}
                            onChange={(e: any) => setFilterAddressee(e.target.value)}
                        />
                    } />

                <Column field="agreementdesc" header={intl.formatMessage({ id: 'Agreement' })} className="columnMedium"
                    body={(bud: BudgetDTO) => <div className='text-left'>{bud.agreementdesc}</div>}
                    filter
                    filterElement={
                        <Combobox
                            getItems={(getItemProps: GetItemsProps) => {
                                return getHealthEntityComboDataDTO(getItemProps).then((res: any) => {
                                    return [defaultAllComboDataDTO, ...res];
                                });
                            }}
                            minPanelWidth={250}
                            optionLabel='name'
                            placeholder={intl.formatMessage({ id: 'Agreement' })}
                            value={filterHealthEntity.name}
                            setValue={(e: ComboDataDTO) => {
                                if (e) setFilterHealthEntity(e);
                            }}
                        />
                    } />

                <Column field="budget" header={'Nº'} className="columnSmall text-center"
                    filter
                    filterElement={
                        <InputTextCustom placeholder={"Nº"}
                            value={filterBudgetId}
                            inputType={EInputTypes.NUMBER}
                            onChange={(e: any) => setFilterBudgetId(e.target.value)}
                        />
                    } />

                <Column field='date' header={intl.formatMessage({ id: "Date" })} className='columnDate text-center'
                    body={(bud: BudgetDTO) => formatDate(getDate(bud.date))}
                    filter
                    filterElement={
                        <InputDate value={filterDate ?? new Date()} onChange={(e) => setFilterDate(e.value)} />
                    } />

                <Column field="detail" header={intl.formatMessage({ id: 'Description' })}
                    body={(bud: BudgetDTO) => <div className='text-left'>{bud.detail}</div>}
                    filter
                    filterElement={
                        <InputTextCustom placeholder={intl.formatMessage({ id: "Detail" })}
                            value={filterDetail}
                            inputType={EInputTypes.TEXT}
                            onChange={(e: any) => setFilterDetail(e.target.value)}
                        />
                    } />

                <Column field="budgetstatedescription" header={"Estado"} className="columnMedium"
                    body={(bud: BudgetDTO) => <div className='text-left'>{bud.budgetstatedescription}</div>}
                    filter
                    filterElement={
                        <Combobox
                            getItems={(getItemProps: GetItemsProps) => {
                                return getBudgetStates(getItemProps).then((res: any) => {
                                    return [defaultAllComboDataDTO, ...res];
                                });
                            }}
                            minPanelWidth={150}
                            optionLabel='name'
                            placeholder="Estado"
                            value={filterState.name}
                            setValue={(e: ComboDataDTO) => setFilterState(e)}
                        />
                    } />

                <Column field="value" className="w-12p columnR" header={intl.formatMessage({ id: 'Total' })}
                    body={(bud: BudgetDTO) => (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>$</span>
                            <span style={{ textAlign: 'right' }}>
                                {bud.total ? bud.total.toLocaleString('es-AR', {
                                    maximumFractionDigits: 2,
                                    style: 'decimal'
                                }) : '0'}
                            </span>
                        </div>
                    )}
                />
            </CustomTable>

        </React.Fragment>
    );
}
